import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Loader2, Target, Users2, Calendar, FileText, Activity, ShieldCheck, Landmark, Mail, Phone, MapPin, UserX, CheckCircle2, XCircle, RotateCcw, Plus, ArrowRightLeft, Save, Eye, Download, FileArchive } from "lucide-react";
import { api } from "../../lib/api";

/**
 * Founder-only right-side drawer that shows a full profile of one staff
 * member: onboarding paperwork, activity + timeline, goals w/ progress bars,
 * their calendar, and their book of clients (with reassignment).
 *
 * Fetches everything from GET /api/admin-auth/staff/{user_id}, refreshes on
 * every action.
 */

const TABS = [
  { id: "overview",   label: "Overview",   icon: Activity   },
  { id: "onboarding", label: "Onboarding", icon: ShieldCheck },
  { id: "goals",      label: "Goals",      icon: Target     },
  { id: "calendar",   label: "Calendar",   icon: Calendar   },
  { id: "clients",    label: "Clients",    icon: Users2     },
  { id: "timeline",   label: "Timeline",   icon: FileText   },
];

const LEGAL_DOCS = [
  { key: "employment_agreement", label: "Employment Agreement" },
  { key: "form_w9",              label: "IRS Form W-9" },
  { key: "form_i9",              label: "Form I-9 · Work Authorization" },
  { key: "direct_deposit_auth",  label: "Direct Deposit Authorization" },
  { key: "confidentiality",      label: "Confidentiality & IP Assignment" },
];

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
};
const fmtDT = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
};
const money = (cents) => (Number(cents || 0) / 100).toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });

export default function StaffDetailDrawer({ userId, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState("overview");
  const [busy, setBusy] = useState(false);
  const [staffList, setStaffList] = useState([]); // for reassign picker

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin-auth/staff/${userId}`);
      setDetail(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load staff details.");
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    if (!userId) return;
    load();
    api.get("/admin-auth/staff").then(({ data }) => setStaffList(data.staff || [])).catch(() => {});
    // eslint-disable-next-line
  }, [userId]);

  const doAction = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(msg);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong.");
    } finally { setBusy(false); }
  };

  if (!userId) return null;

  const user = detail?.user;
  const onb = detail?.onboarding;
  const activity = detail?.activity;
  const timeline = detail?.timeline || [];
  const goals = detail?.goals;
  const gp = detail?.goal_progress;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="staff-drawer">
      {/* backdrop */}
      <div
        onClick={onClose}
        className="flex-1 bg-black/40 backdrop-blur-sm"
        data-testid="staff-drawer-backdrop"
      />
      <aside className="w-full max-w-3xl h-full bg-[#eae7de] shadow-2xl border-l border-[#1e3d3a]/15 overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#eae7de]/95 backdrop-blur-md border-b border-[#1e3d3a]/12">
          <div className="flex items-start justify-between px-6 pt-5 pb-3">
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.28em] text-[#a3823f]">Staff profile</p>
              <h2 className="font-head text-2xl mt-1 text-[#1e3d3a] truncate">
                {user?.display_name || user?.username || "Loading…"}
              </h2>
              <p className="text-[11px] uppercase tracking-[0.22em] text-[#1e3d3a]/55">
                @{user?.username} · {user?.role} · {user?.status}
                {onb?.status === "finalized" && " · onboarding cleared"}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              data-testid="staff-drawer-close"
              className="rounded-full p-1.5 hover:bg-[#1e3d3a]/8 text-[#1e3d3a]/55 hover:text-[#1e3d3a]"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Tabs */}
          <nav className="px-6 pb-3 flex gap-1.5 flex-wrap" data-testid="staff-drawer-tabs">
            {TABS.map((t) => {
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  data-testid={`staff-drawer-tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] transition-colors duration-300 ${
                    active
                      ? "bg-[#1e3d3a] text-white"
                      : "border border-[#1e3d3a]/12 text-[#1e3d3a]/60 hover:text-[#1e3d3a] hover:border-[#c9a961]"
                  }`}
                >
                  <t.icon className="h-3 w-3" />
                  {t.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Body */}
        <div className="px-6 py-6">
          {!detail && busy && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-5 w-5 animate-spin text-[#c9a961]" />
            </div>
          )}

          {detail && tab === "overview" && (
            <OverviewPane detail={detail} onAction={doAction} busy={busy} />
          )}
          {detail && tab === "onboarding" && (
            <OnboardingPane onb={onb} userId={userId} username={user?.username} />
          )}
          {detail && tab === "goals" && (
            <GoalsPane userId={userId} goals={goals} progress={gp} onSaved={load} />
          )}
          {detail && tab === "calendar" && (
            <CalendarPane userId={userId} bookings={activity?.bookings || []} onCreated={load} />
          )}
          {detail && tab === "clients" && (
            <ClientsPane userId={userId} clients={activity?.clients || []} staffList={staffList} onReassigned={load} />
          )}
          {detail && tab === "timeline" && (
            <TimelinePane timeline={timeline} />
          )}
        </div>
      </aside>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Overview
// -----------------------------------------------------------------------------
function OverviewPane({ detail, onAction, busy }) {
  const { user, activity, goal_progress, terminated, onboarding } = detail;
  const canFire = user.role !== "founder" && user.status !== "terminated";
  const canReinstate = user.status === "terminated";
  const canFinalize = onboarding?.status === "submitted";

  return (
    <div className="space-y-6" data-testid="staff-drawer-overview">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Leads" val={activity.counts.leads} />
        <StatCard label="Bookings" val={activity.counts.bookings} />
        <StatCard label="Clients" val={activity.counts.clients} />
        <StatCard label="Savings" val={money(activity.total_savings_cents)} />
      </div>

      {goal_progress && (
        <div className="rounded-2xl border border-[#c9a961]/40 bg-white p-4">
          <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f] mb-3">Current period · goal progress</p>
          <div className="space-y-3">
            <GoalBar label="Leads"        actual={goal_progress.leads.actual}        target={goal_progress.leads.target}        pct={goal_progress.leads.pct} />
            <GoalBar label="Bookings"     actual={goal_progress.bookings.actual}     target={goal_progress.bookings.target}     pct={goal_progress.bookings.pct} />
            <GoalBar label="Clients"      actual={goal_progress.clients.actual}      target={goal_progress.clients.target}      pct={goal_progress.clients.pct} />
            <GoalBar label="Savings"      actual={money(goal_progress.savings_cents.actual)} target={money(goal_progress.savings_cents.target)} pct={goal_progress.savings_cents.pct} />
          </div>
        </div>
      )}

      {terminated && (
        <div className="rounded-2xl border border-[#b23a48]/30 bg-[#b23a48]/8 p-4" data-testid="staff-drawer-terminated-banner">
          <p className="text-[10px] uppercase tracking-[0.24em] text-[#b23a48]">Terminated</p>
          <p className="text-sm text-[#1e3d3a] mt-1">
            {fmt(terminated.at)} by {terminated.by || "—"}
            {terminated.reason && ` — "${terminated.reason}"`}
          </p>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {canFinalize && (
          <button
            type="button"
            data-testid="staff-drawer-finalize"
            disabled={busy}
            onClick={() => onAction(() => api.patch(`/admin-auth/onboarding/${user.id}/finalize`), "Onboarding finalized.")}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#a3823f] text-white px-4 py-2 text-[11px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-60 transition-colors duration-300"
          >
            <ShieldCheck className="h-3.5 w-3.5" /> Finalize onboarding
          </button>
        )}
        {canFire && (
          <button
            type="button"
            data-testid="staff-drawer-terminate"
            disabled={busy}
            onClick={() => {
              const reason = window.prompt("Terminate this staff member?\n\nOptional: enter the reason (kept in audit log).", "");
              if (reason === null) return;
              onAction(() => api.post(`/admin-auth/users/${user.id}/terminate`, { reason }), "Terminated.");
            }}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#b23a48]/40 text-[#b23a48] px-4 py-2 text-[11px] uppercase tracking-[0.22em] hover:bg-[#b23a48]/10 disabled:opacity-60 transition-colors duration-300"
          >
            <UserX className="h-3.5 w-3.5" /> Fire
          </button>
        )}
        {canReinstate && (
          <button
            type="button"
            data-testid="staff-drawer-reinstate"
            disabled={busy}
            onClick={() => onAction(() => api.post(`/admin-auth/users/${user.id}/reinstate`), "Reinstated.")}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#6b7c3a]/40 text-[#4d5a24] px-4 py-2 text-[11px] uppercase tracking-[0.22em] hover:bg-[#6b7c3a]/10 disabled:opacity-60 transition-colors duration-300"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Reinstate
          </button>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Onboarding paperwork
// -----------------------------------------------------------------------------
function OnboardingPane({ onb, userId, username }) {
  const p = onb?.profile || {};
  const docs = onb?.signed_docs || {};
  const [downloading, setDownloading] = useState(null); // doc key OR "zip"

  // Streams a signed doc from the API and either opens it in a new tab
  // (preview) or downloads it as a file. Uses a blob so the auth cookie is
  // sent by the axios interceptor.
  const fetchDoc = async (key, mode) => {
    setDownloading(key);
    try {
      const isZip = key === "__zip__";
      const url = isZip
        ? `/admin-auth/staff/${userId}/documents.zip`
        : `/admin-auth/staff/${userId}/documents/${key}`;
      const { data } = await api.get(url, { responseType: "blob" });
      const blob = new Blob([data], { type: isZip ? "application/zip" : "application/pdf" });
      const href = window.URL.createObjectURL(blob);
      if (mode === "preview") {
        window.open(href, "_blank", "noopener,noreferrer");
        // Revoke after a moment — the tab has its own reference by then.
        setTimeout(() => window.URL.revokeObjectURL(href), 30000);
      } else {
        const a = document.createElement("a");
        a.href = href;
        a.download = isZip
          ? `elevare-${username || "staff"}-onboarding-docs.zip`
          : `elevare-${username || "staff"}-${key}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(href);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not fetch the document.");
    } finally {
      setDownloading(null);
    }
  };

  if (!onb || onb.status === "incomplete") {
    return (
      <div className="rounded-2xl border border-[#c9a961]/40 bg-[#c9a961]/8 p-5 text-center text-[13px] text-[#1e3d3a]" data-testid="staff-drawer-onboarding-empty">
        This staff member has not submitted their onboarding yet.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="staff-drawer-onboarding">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <InfoCard title="Personal">
          <Row icon={Mail}   label="Legal name" val={p.legal_name} />
          <Row icon={Phone}  label="Phone" val={p.phone} />
          <Row icon={Mail}   label="Email" val={p.email} />
          <Row icon={Mail}   label="DOB"   val={p.dob || "—"} />
          <Row icon={Mail}   label="SSN (last 4)" val={p.ssn_last4 ? `•••-••-${p.ssn_last4}` : "—"} />
        </InfoCard>
        <InfoCard title="Address">
          <Row icon={MapPin} label="Street" val={p.address_line1} />
          {p.address_line2 && <Row icon={MapPin} label="Apt/Suite" val={p.address_line2} />}
          <Row icon={MapPin} label="City" val={p.city} />
          <Row icon={MapPin} label="State" val={p.state} />
          <Row icon={MapPin} label="ZIP" val={p.postal_code} />
        </InfoCard>
        <InfoCard title="Direct deposit">
          <Row icon={Landmark} label="Bank name" val={p.bank_name} />
          <Row icon={Landmark} label="Routing" val={p.bank_routing} />
          <Row icon={Landmark} label="Account" val={onb.bank_last4 ? `••••${onb.bank_last4}` : "—"} />
          <Row icon={Landmark} label="Type" val={p.bank_account_type} />
        </InfoCard>
        <InfoCard title="Submission">
          <Row icon={Mail} label="Submitted" val={fmt(onb.submitted_at)} />
          <Row icon={Mail} label="Finalized" val={onb.finalized_at ? `${fmt(onb.finalized_at)} by ${onb.finalized_by}` : "—"} />
          <Row icon={ShieldCheck} label="Typed signature" val={onb.typed_signature} />
        </InfoCard>
      </div>

      <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-4" data-testid="staff-drawer-signed-docs">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f]">Signed documents</p>
          <button
            type="button"
            data-testid="staff-docs-download-zip"
            onClick={() => fetchDoc("__zip__", "download")}
            disabled={downloading === "__zip__"}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] text-white px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-60 transition-colors duration-300"
          >
            {downloading === "__zip__" ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileArchive className="h-3 w-3" />}
            Download all (.zip)
          </button>
        </div>
        <ul className="space-y-2">
          {LEGAL_DOCS.map((d) => {
            const state = docs[d.key] || {};
            const ok = !!state.agreed;
            const busy = downloading === d.key;
            return (
              <li key={d.key} data-testid={`staff-doc-${d.key}`} className="flex items-center justify-between gap-3 border-b border-[#1e3d3a]/6 py-2.5 last:border-b-0">
                <div className="flex items-start gap-2 min-w-0 flex-1">
                  {ok ? <CheckCircle2 className="h-4 w-4 text-[#6b7c3a] mt-0.5 shrink-0" /> : <XCircle className="h-4 w-4 text-[#b23a48] mt-0.5 shrink-0" />}
                  <div className="min-w-0">
                    <p className="text-[13px] text-[#1e3d3a] truncate">{d.label}</p>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/50 truncate">
                      {ok ? `Signed as "${state.signed_name || onb.typed_signature || "—"}"` : "Not signed"}
                    </p>
                  </div>
                </div>
                {ok && (
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      data-testid={`staff-doc-view-${d.key}`}
                      onClick={() => fetchDoc(d.key, "preview")}
                      disabled={busy}
                      title="Preview in new tab"
                      className="inline-flex items-center gap-1 rounded-full border border-[#1e3d3a]/15 text-[#1e3d3a]/70 px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] hover:text-[#1e3d3a] hover:border-[#c9a961] disabled:opacity-60 transition-colors duration-300"
                    >
                      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3" />}
                      View
                    </button>
                    <button
                      type="button"
                      data-testid={`staff-doc-download-${d.key}`}
                      onClick={() => fetchDoc(d.key, "download")}
                      disabled={busy}
                      title="Download PDF"
                      className="inline-flex items-center gap-1 rounded-full bg-[#c9a961]/15 text-[#a3823f] px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-60 transition-colors duration-300"
                    >
                      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
                      PDF
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Goals
// -----------------------------------------------------------------------------
function GoalsPane({ userId, goals, progress, onSaved }) {
  const [form, setForm] = useState({
    period: goals?.period || "monthly",
    leads: goals?.targets?.leads ?? 0,
    bookings: goals?.targets?.bookings ?? 0,
    clients: goals?.targets?.clients ?? 0,
    savings_dollars: Math.round(((goals?.targets?.savings_cents) ?? 0) / 100),
    notes: goals?.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/admin-auth/staff/${userId}/goals`, {
        period: form.period,
        targets: {
          leads: Number(form.leads) || 0,
          bookings: Number(form.bookings) || 0,
          clients: Number(form.clients) || 0,
          savings_cents: Math.max(0, Math.round(Number(form.savings_dollars) || 0) * 100),
        },
        notes: form.notes || null,
      });
      toast.success("Goals saved.");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save goals."); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="staff-drawer-goals">
      {progress && (
        <div className="rounded-2xl border border-[#c9a961]/40 bg-white p-4">
          <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f] mb-3">Live progress</p>
          <div className="space-y-3">
            <GoalBar label="Leads"    actual={progress.leads.actual}    target={progress.leads.target}    pct={progress.leads.pct} />
            <GoalBar label="Bookings" actual={progress.bookings.actual} target={progress.bookings.target} pct={progress.bookings.pct} />
            <GoalBar label="Clients"  actual={progress.clients.actual}  target={progress.clients.target}  pct={progress.clients.pct} />
            <GoalBar label="Savings"  actual={money(progress.savings_cents.actual)} target={money(progress.savings_cents.target)} pct={progress.savings_cents.pct} />
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-4">
        <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f] mb-3">Set targets</p>
        <div className="grid grid-cols-2 gap-3">
          <NumField label="Leads goal"    testid="goal-leads"    value={form.leads}    onChange={(v)=>setForm({...form, leads:v})} />
          <NumField label="Bookings goal" testid="goal-bookings" value={form.bookings} onChange={(v)=>setForm({...form, bookings:v})} />
          <NumField label="Clients goal"  testid="goal-clients"  value={form.clients}  onChange={(v)=>setForm({...form, clients:v})} />
          <NumField label="Savings $"     testid="goal-savings"  value={form.savings_dollars} onChange={(v)=>setForm({...form, savings_dollars:v})} />
          <div>
            <label className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55 mb-1 block">Period</label>
            <select
              data-testid="goal-period"
              value={form.period}
              onChange={(e)=>setForm({...form, period: e.target.value})}
              className="w-full rounded-lg border border-[#1e3d3a]/15 bg-[#eae7de] px-3 py-2 text-sm focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/25"
            >
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
            </select>
          </div>
          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55 mb-1 block">Notes for {"the hire (private)"}</label>
            <textarea
              data-testid="goal-notes"
              rows={3}
              value={form.notes}
              onChange={(e)=>setForm({...form, notes: e.target.value})}
              placeholder="Coaching notes, focus areas, etc."
              className="w-full rounded-lg border border-[#1e3d3a]/15 bg-[#eae7de] px-3 py-2 text-sm focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/25"
            />
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <p className="text-[10px] text-[#1e3d3a]/45">
            {goals ? `Last updated by ${goals.set_by} · ${fmt(goals.updated_at)}` : "No goals set yet."}
          </p>
          <button
            type="button"
            data-testid="goal-save"
            disabled={saving}
            onClick={save}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] text-white px-5 py-2 text-[11px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-60 transition-colors duration-300"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save goals
          </button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Calendar
// -----------------------------------------------------------------------------
function CalendarPane({ userId, bookings, onCreated }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ date: "", time: "", name: "", email: "", company: "", goal: "" });
  const [saving, setSaving] = useState(false);

  const now = Date.now();
  const upcoming = bookings.filter((b) => new Date(b.slot_iso).getTime() >= now);
  const past = bookings.filter((b) => new Date(b.slot_iso).getTime() < now);

  const submit = async () => {
    if (!form.date || !form.time || !form.name || !form.email) {
      toast.error("Date, time, name and email are required.");
      return;
    }
    setSaving(true);
    try {
      const slot_iso = new Date(`${form.date}T${form.time}:00`).toISOString();
      await api.post(`/admin-auth/staff/${userId}/bookings`, {
        slot_iso, name: form.name, email: form.email, company: form.company || null, goal: form.goal || null,
      });
      toast.success("Booking added.");
      setForm({ date: "", time: "", name: "", email: "", company: "", goal: "" });
      setShowForm(false);
      onCreated?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add booking."); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="staff-drawer-calendar">
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f]">
          {upcoming.length} upcoming · {past.length} past
        </p>
        <button
          type="button"
          data-testid="staff-drawer-calendar-add"
          onClick={() => setShowForm((v) => !v)}
          className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] text-white px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300"
        >
          {showForm ? <X className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
          {showForm ? "Cancel" : "Add booking"}
        </button>
      </div>

      {showForm && (
        <div className="rounded-2xl border border-[#c9a961]/40 bg-white p-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date" testid="cal-date"    type="date" value={form.date}    onChange={(v)=>setForm({...form, date:v})} />
            <Field label="Time" testid="cal-time"    type="time" value={form.time}    onChange={(v)=>setForm({...form, time:v})} />
            <Field label="Name" testid="cal-name"    value={form.name}    onChange={(v)=>setForm({...form, name:v})} />
            <Field label="Email" testid="cal-email"  value={form.email}   onChange={(v)=>setForm({...form, email:v})} type="email" />
            <Field label="Company" testid="cal-company" value={form.company} onChange={(v)=>setForm({...form, company:v})} className="col-span-2" />
            <Field label="Goal" testid="cal-goal"    value={form.goal}    onChange={(v)=>setForm({...form, goal:v})} className="col-span-2" />
          </div>
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              data-testid="cal-submit"
              disabled={saving}
              onClick={submit}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#a3823f] text-white px-5 py-2 text-[11px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-60 transition-colors duration-300"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Add booking
            </button>
          </div>
        </div>
      )}

      <BookingList title="Upcoming" bookings={upcoming} emptyMsg="No upcoming calls on their calendar." tone="gold" />
      <BookingList title="Past"     bookings={past}     emptyMsg="No past calls yet."                    tone="quiet" />
    </div>
  );
}

function BookingList({ title, bookings, emptyMsg, tone }) {
  return (
    <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-4">
      <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f] mb-2">{title}</p>
      {bookings.length === 0 ? (
        <p className="text-[12px] text-[#1e3d3a]/45 py-2">{emptyMsg}</p>
      ) : (
        <ul className="divide-y divide-[#1e3d3a]/8">
          {bookings.map((b) => (
            <li key={b.id} data-testid={`staff-booking-${b.id}`} className="py-2.5 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[13px] text-[#1e3d3a] truncate">{b.name || b.email}</p>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/50 truncate">
                  {b.company || "—"} · {b.email}
                </p>
              </div>
              <span className={`text-[11px] tabular-nums ${tone === "quiet" ? "text-[#1e3d3a]/45" : "text-[#a3823f]"}`}>
                {fmtDT(b.slot_iso)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Clients
// -----------------------------------------------------------------------------
function ClientsPane({ userId, clients, staffList, onReassigned }) {
  const [reassigning, setReassigning] = useState(null); // client_id
  const [targetId, setTargetId] = useState("");

  const eligible = useMemo(
    () => (staffList || []).filter((s) => s.id !== userId && s.status !== "terminated"),
    [staffList, userId],
  );

  const submit = async (clientId) => {
    if (!targetId) { toast.error("Choose a new owner."); return; }
    try {
      const target = staffList.find((s) => s.id === targetId);
      await api.post(`/admin-auth/staff/${userId}/clients/${clientId}/reassign`, {
        new_owner_id: targetId === "unassigned" ? "" : targetId,
        new_owner_username: target?.username || null,
      });
      toast.success("Client reassigned.");
      setReassigning(null);
      setTargetId("");
      onReassigned?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reassign."); }
  };

  return (
    <div className="space-y-3" data-testid="staff-drawer-clients">
      <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f]">
        {clients.length} client{clients.length === 1 ? "" : "s"} in their book
      </p>
      {clients.length === 0 ? (
        <p className="text-[13px] text-[#1e3d3a]/50 py-6 text-center">
          They haven&rsquo;t converted any clients yet.
        </p>
      ) : (
        <ul className="rounded-2xl border border-[#1e3d3a]/12 bg-white divide-y divide-[#1e3d3a]/8">
          {clients.map((c) => (
            <li key={c.client_id} data-testid={`staff-client-${c.client_id}`} className="p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[13px] text-[#1e3d3a] truncate">{c.business_name || c.client_id}</p>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/50 truncate">
                    {c.client_id}
                  </p>
                </div>
                <button
                  type="button"
                  data-testid={`staff-reassign-${c.client_id}`}
                  onClick={() => { setReassigning(reassigning === c.client_id ? null : c.client_id); setTargetId(""); }}
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] text-[#a3823f] hover:text-[#1e3d3a] transition-colors duration-300"
                >
                  <ArrowRightLeft className="h-3 w-3" /> Reassign
                </button>
              </div>
              {reassigning === c.client_id && (
                <div className="mt-3 flex flex-wrap items-center gap-2" data-testid={`staff-reassign-form-${c.client_id}`}>
                  <select
                    value={targetId}
                    onChange={(e)=>setTargetId(e.target.value)}
                    data-testid={`staff-reassign-select-${c.client_id}`}
                    className="rounded-lg border border-[#1e3d3a]/15 bg-[#eae7de] px-3 py-1.5 text-sm focus:border-[#c9a961] focus:outline-none"
                  >
                    <option value="">Choose new owner…</option>
                    <option value="unassigned">Unassigned (founder pool)</option>
                    {eligible.map((s) => (
                      <option key={s.id} value={s.id}>{s.display_name || s.username} · @{s.username}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    data-testid={`staff-reassign-submit-${c.client_id}`}
                    onClick={() => submit(c.client_id)}
                    className="inline-flex items-center gap-1 rounded-full bg-[#1e3d3a] text-white px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300"
                  >
                    Confirm
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Timeline
// -----------------------------------------------------------------------------
function TimelinePane({ timeline }) {
  const iconFor = (kind) => {
    if (kind === "lead_added")       return Mail;
    if (kind === "booking")          return Calendar;
    if (kind === "client_converted") return Users2;
    return Activity;
  };

  return (
    <div className="space-y-2" data-testid="staff-drawer-timeline">
      {timeline.length === 0 && (
        <p className="text-[13px] text-[#1e3d3a]/45 py-6 text-center">No activity yet.</p>
      )}
      <ul className="rounded-2xl border border-[#1e3d3a]/12 bg-white divide-y divide-[#1e3d3a]/8">
        {timeline.map((t, i) => {
          const Icon = iconFor(t.kind);
          return (
            <li key={i} data-testid={`staff-timeline-${i}`} className="p-3 flex items-center gap-3">
              <div className="rounded-full bg-[#c9a961]/15 p-1.5 shrink-0">
                <Icon className="h-3.5 w-3.5 text-[#a3823f]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] text-[#1e3d3a] truncate">{t.label}</p>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/45">{fmtDT(t.at)}</p>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Bits
// -----------------------------------------------------------------------------
function StatCard({ label, val }) {
  return (
    <div className="rounded-xl border border-[#1e3d3a]/10 bg-white p-3">
      <p className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/50">{label}</p>
      <p className="font-head text-2xl mt-1 text-[#1e3d3a] tabular-nums">{val}</p>
    </div>
  );
}

function GoalBar({ label, actual, target, pct }) {
  const capped = Math.min(100, pct);
  const overshoot = pct > 100;
  return (
    <div>
      <div className="flex items-baseline justify-between text-[10px] uppercase tracking-[0.22em]">
        <span className="text-[#1e3d3a]/60">{label}</span>
        <span className="tabular-nums text-[#a3823f]">{actual} / {target} · {pct}%</span>
      </div>
      <div className="mt-1 h-1.5 rounded-full bg-[#1e3d3a]/8 overflow-hidden">
        <div
          className={`h-full transition-all duration-500 ${overshoot ? "bg-[#6b7c3a]" : "bg-[#c9a961]"}`}
          style={{ width: `${capped}%` }}
        />
      </div>
    </div>
  );
}

function InfoCard({ title, children }) {
  return (
    <div className="rounded-xl border border-[#1e3d3a]/10 bg-white p-4">
      <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f] mb-2">{title}</p>
      <ul className="space-y-1">{children}</ul>
    </div>
  );
}

function Row({ icon: Icon, label, val }) {
  return (
    <li className="flex items-baseline gap-2">
      <Icon className="h-3 w-3 text-[#c9a961] shrink-0" />
      <span className="text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/50">{label}</span>
      <span className="ml-auto text-right text-[12px] text-[#1e3d3a] truncate">{val || "—"}</span>
    </li>
  );
}

function Field({ label, value, onChange, testid, type = "text", className = "" }) {
  return (
    <div className={className}>
      <label className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55 mb-1 block">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-[#1e3d3a]/15 bg-[#eae7de] px-3 py-2 text-sm focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/25"
      />
    </div>
  );
}

function NumField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55 mb-1 block">{label}</label>
      <input
        data-testid={testid}
        type="number"
        min="0"
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/^0+(?=\d)/, ""))}
        className="w-full rounded-lg border border-[#1e3d3a]/15 bg-[#eae7de] px-3 py-2 text-sm tabular-nums focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/25"
      />
    </div>
  );
}
